/* ============================================================
   ProviderEarningsPanel — CB-4 (TASK-cf5f4532): read-only
   «Доходы» tab of the performer cabinet. cabinet-gap-audit §4/§5
   P4: per-order commission/settlement rows from the SD-1/SD-2
   snapshot fields + monthly aggregates + the legacy «без Safe
   Deal» empty state. Payout buttons wait for Artem's §12
   answers — this tab only shows where the money sits.

   Light theme, Montserrat, iOS-18 minimal per DESIGN-SYSTEM.md
   (no gradients, no heavy shadows).
   ============================================================ */

import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { ProviderEarnings } from '../types/provider';
import {
  formatRub,
  formatRubPlain,
  groupByMonth,
  isEmptyEarnings,
  paidDate,
  payoutHint,
  stateLabel,
  stateTone,
} from './earningsView';

const TONE_COLOR: Record<'success' | 'warning' | 'danger', string> = {
  success: 'var(--color-success, #34C759)',
  warning: 'var(--color-warning, #b25000)',
  danger: 'var(--color-danger, #FF3B30)',
};

export function ProviderEarningsPanel() {
  const [earnings, setEarnings] = useState<ProviderEarnings | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      setEarnings(await api.getProviderEarnings());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить доходы');
      setEarnings(null);
    }
  };

  useEffect(() => { load(); }, []);

  if (error) {
    return (
      <div className="card" style={{ padding: 'var(--space-lg)', textAlign: 'center' }}>
        <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-danger)', margin: 0 }}>{error}</p>
        <button className="btn btn-secondary" onClick={load} style={{ marginTop: 'var(--space-md)' }}>
          Повторить
        </button>
      </div>
    );
  }

  if (!earnings) {
    return (
      <div className="card" style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-lg)' }}>
        <div className="spinner" />
      </div>
    );
  }

  if (isEmptyEarnings(earnings)) {
    return (
      <div className="card" style={{ padding: 'var(--space-lg)' }}>
        <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)', margin: 0 }}>
          Пока нет оплат через Safe Deal
        </p>
        <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-sm)', marginBottom: 0 }}>
          Здесь появятся начисления по заказам, оплаченным через безопасную сделку:
          сумма заказа, комиссия платформы и то, что причитается вам.
        </p>
      </div>
    );
  }

  const months = groupByMonth(earnings.rows);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--space-sm)' }}>
        <StatCard label="Выплачено" value={formatRub(earnings.paidOutRub)} />
        <StatCard label="В эскроу" value={formatRub(earnings.inEscrowRub)} />
        <StatCard label="Возвращено клиентам" value={formatRub(earnings.refundedRub)} />
        <StatCard
          label="Комиссия платформы"
          value={formatRub(earnings.platformFeeRub)}
          hint={`из ${formatRub(earnings.grossRub)} оплачено`}
        />
      </div>
      {earnings.pendingPayoutAttempts > 0 && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>
          Выплат в пути: {earnings.pendingPayoutAttempts} — деньги уже покинули сделку, ждём подтверждения банка.
        </p>
      )}

      {/* Monthly aggregates */}
      <div className="card" style={{ padding: 'var(--space-lg)' }}>
        <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)', margin: 0, marginBottom: 'var(--space-sm)' }}>
          По месяцам
        </p>
        {months.map((m, i) => (
          <div
            key={m.key}
            style={{
              display: 'flex', alignItems: 'baseline', gap: 'var(--space-sm)',
              padding: 'var(--space-xs) 0',
              borderBottom: i < months.length - 1 ? '1px solid var(--color-separator)' : 'none',
            }}
          >
            <span style={{ flex: 1, fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-medium)' }}>{m.label}</span>
            <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
              {m.count} заказ(ов) · комиссия {formatRubPlain(m.feeRub)} ₽
            </span>
            <span style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', whiteSpace: 'nowrap' }}>
              {formatRub(m.netRub)}
            </span>
          </div>
        ))}
      </div>

      {/* Per-order rows */}
      <div className="card" style={{ padding: 'var(--space-lg)' }}>
        <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-semibold)', margin: 0, marginBottom: 'var(--space-sm)' }}>
          По заказам
        </p>
        {earnings.rows.map((row, i) => {
          const hint = payoutHint(row);
          return (
            <div
              key={row.orderId}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-sm)',
                padding: 'var(--space-xs) 0',
                borderBottom: i < earnings.rows.length - 1 ? '1px solid var(--color-separator)' : 'none',
              }}
            >
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                {paidDate(row)}
              </span>
              <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-tertiary)' }}>
                  …{row.orderId.slice(-6)}
                </span>
                <span
                  className="badge"
                  style={{
                    fontSize: 'var(--fs-caption)',
                    color: TONE_COLOR[stateTone(row.state)],
                    background: 'var(--color-separator)',
                    borderRadius: 'var(--radius-pill)',
                    padding: '1px 8px',
                    fontWeight: 'var(--fw-medium)',
                  }}
                >
                  {stateLabel(row.state)}
                </span>
                {hint && (
                  <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-tertiary)' }}>{hint}</span>
                )}
              </span>
              <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                −{formatRubPlain(row.platformFeeRub)} ₽ комиссия
              </span>
              <span style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)', whiteSpace: 'nowrap' }}>
                {formatRub(row.netRub)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="card" style={{ padding: 'var(--space-md)' }}>
      <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>{label}</p>
      <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)', margin: 'var(--space-xs) 0 0 0' }}>{value}</p>
      {hint && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-tertiary)', margin: 'var(--space-xs) 0 0 0' }}>{hint}</p>
      )}
    </div>
  );
}
